import React, { Fragment } from 'react';
import AddToCart from "./AddToCart";
import { translate } from "../Services/translation";

/**
 * @typedef {Object} Currency
 * @property {number} exchangeRate - The exchange rate of the currency.
 * @property {string|null} groupSeparator - The group separator used in the currency format.
 * @property {string} id - The ID of the currency.
 * @property {boolean} isBaseCurrency - Indicates if this is the base currency.
 * @property {string} symbol - The symbol of the currency.
 * @property {number} symbolPosition - The position of the currency symbol.
 * @property {string} systemId - The system ID of the currency.
 * @property {string} textFormat - The text format for the currency.
 */

/**
 * @typedef {Object} PriceDetails
 * @property {number} minimumQuantity - The minimum quantity for the price.
 * @property {number} price - The price of the product.
 * @property {number} priceWithVat - The price of the product including VAT.
 * @property {string} unitOfMeasurementSystemId - The system ID for the unit of measurement.
 * @property {string|null} unitOfMeasurementTitle - The title of the unit of measurement.
 * @property {number} vatPercentage - The VAT percentage applied to the price.
 */

/**
 * @typedef {Object} Price
 * @property {PriceDetails} campaignPrice - The campaign price if available.
 * @property {string} formattedCampaignPrice - The formatted campaign price.
 * @property {Currency} currency - The currency details.
 * @property {boolean} hasPrice - Indicates if the product has a price.
 * @property {PriceDetails} price - The price details.
 * @property {string} formattedPrice - The formatted price.
 * @property {Array} tierPrices - The tier prices if available.
 */

/**
 * @typedef {Object} Product
 * @property {string} articleNumber - The article number of the product.
 * @property {string} id - The ID of the product.
 * @property {boolean} isVariant - Indicates if the product is a variant.
 * @property {boolean} isExactMatch - Indicates if the product is an exact match based on article number.
 * @property {boolean} showQuantityField - Indicates if the quantity field should be shown.
 * @property {boolean} isInStock - Indicates if the product is in stock.
 * @property {boolean} isAnonymousUser - Indicates if the user is anonymous.
 * @property {Price} price - The price details of the product.
 * @property {string} quantityFieldId - The ID of the quantity field.
 * @property {boolean} hidePrice - Indicates if the price should be hidden.
 * @property {string} category - The category of the product.
 * @property {string|null} categoryLabel - The label of the category in a different language if available.
 * @property {string} name - The name of the product.
 * @property {string} url - The URL to the product page.
 * @property {string} imageSource - The source path for the product image.
 * @property {boolean} hasImage - Indicates if the product has an image.
 * @property {boolean} showAll - Flag to show the "show all results" button.
 */

/**
 * @param {Product} item
 * @returns {boolean}
 */
const isItemBuyable = (item) => {
    return !item.isAnonymousUser && item.isExactMatch && item?.price?.hasPrice;
};

const isQuotationProduct = (item) => {
    return item?.price?.price?.price == 0 || item.hidePrice;
};

const SearchResultItem = ({item, searchUrl, index, selectedItem}) => {

    let liClass = `quick-search-result__item ${item.showAll ? 'hide' : ''} ${
        selectedItem === index
            ? 'quick-search-result__item--selected'
            : ''
    }`.trim();

    let linkClass = item.showAll
        ? 'hide'
        : `quick-search-result__link ${
            item.url
                ? ''
                : 'quick-search-result__link--disabled'
        } ${!isItemBuyable(item) ? 'visitor' : ''}`.trim();

    return (
        <li className={liClass}>
            <a className={linkClass} href={item.showAll ? searchUrl : ((item.replacementProductUrl && !item.isAnonymousUser) ? item.replacementProductUrl : item.url)}>
                {item.hasImage && item.imageSource && (
                    <div className='quick-search-result__image-container'>
                        <img className="quick-search-result__image" src={item.imageSource} />
                    </div>
                )}

                <div className='quick-search-result__name-and-sku'>
                    <div className='quick-search-result__name'>{item.name}</div>

                    {typeof item.articleNumber !== 'undefined' && item.articleNumber.length > 0 && (
                        <div className='quick-search-result__sku'>{translate('nmas.quicksearchheader.resultitem.articlenr.label')}: {item.articleNumber}</div>
                    )}
                </div>
            </a>

            {isItemBuyable(item) && <div className='quick-search-result__add-to-cart'>
            <AddToCart 
                    variant={item}
                    isQuotationProduct={isQuotationProduct(item)}
                ></AddToCart>
            </div>}
        </li>
    );
}

const QuickSearchResultSubList = ({result, searchUrl, label}) => {
    if (result.length) {
        let first = result[0];
        label = first.categoryLabel;
    }

    let windowWidth = window.innerWidth;
    let maxItemsToRender = windowWidth < 992 ? 4 : 8;

    return (
        <>
            <li className="quick-search-result__item quick-search-result__group-header">
                {label}
            </li>

            {result.length > 0 ? result.map((item, index) => (
                <Fragment key={`${item.name}-${index}`}>
                    {index < maxItemsToRender ? (
                        <SearchResultItem {...{ item, index, searchUrl }}/>
                    ) : null}
                </Fragment>
            )) : (
                <Fragment key={`${label}-nohit`}>
                    <li className="quick-search-result__item quick-search-result__no-hit">
                        <div className="quick-search-result__link">{translate('nmas.search.nohit')}</div>
                    </li>
                </Fragment>
            )}
        </>
    );
}


/**
 *
 * @param {Product[]} result
 * @param selectedItem
 * @param searchUrl
 * @returns {JSX.Element}
 * @constructor
 */
const QuickSearchResult = ({result, selectedItem, searchUrl}) => {
    let showAllItem;

    if (result && result.length) {
        let showAllFilterResult = result.filter(x => x.showAll === true);
        if (showAllFilterResult.length) {
            showAllItem = showAllFilterResult[0];
        }
    }

    let hasExactMatch = result.some(r => typeof r.isExactMatch !== 'undefined' && r.isExactMatch === true);
    let productColClasses = hasExactMatch ? "small-12" : "small-12 large-7";

    let resultProducts = result.filter(p => p.category == "Products" || p.category === "ExactMatch");
    let resultBrands = result.filter(p => p.category == "Brands");
    let resultCategories = result.filter(p => p.category == "Categories");

    return (
        <div className="quick-search-result">
            <div className="quick-search-result__list-container">
                <div className="quick-search-result__list row">
                    <div
                        className={`columns ${productColClasses} quick-search-result__sub-list quick-search-result__sub-list--products`}>
                        <QuickSearchResultSubList {...{ result: resultProducts, searchUrl, selectedItem, label: translate('nmas.quicksearchheader.products')}}/>
                    </div>

                    {!hasExactMatch && (
                        <div className="columns small-12 large-5 py-3 bg:ice quick-search-result__other-results">
                            <div className="row">
                                <div className="columns small-12 medium-6 large-12 xlarge-6 quick-search-result__sub-list">
                                    <QuickSearchResultSubList {...{ result: resultCategories, searchUrl, selectedItem, label: translate('nmas.quicksearchheader.categories') }}/>
                                </div>

                                <div className="columns small-12 medium-6 large-12 xlarge-6 quick-search-result__sub-list">
                                    <QuickSearchResultSubList {...{ result: resultBrands, searchUrl, selectedItem, label: translate('nmas.quicksearchheader.brands') }}/>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showAllItem && (
                <div className='quick-search-result__show-all-container'>
                    <a
                        className='button button-primary quick-search-result__show-all'
                        href={searchUrl}
                    >{showAllItem.name}</a>
                </div>
            )}
        </div>
    );
}

export default QuickSearchResult;
